import React, { useEffect, useMemo, useRef, useState } from 'react';
import { apiGet, apiPost, buildApiUrl } from '../../Api';
import {
    MEDIA_VARIANTS,
    mediaCacheKey,
    mediaDimensions,
    mediaPath,
    resolveMediaVariant,
} from '../../media';
import './ImageLoader.css';

const mediaCache = new Map();
const pendingMedia = new Map();
export const MAX_MEDIA_CACHE_ENTRIES = 200;

function cacheMedia(key, url) {
    const previous = mediaCache.get(key);
    if (previous && previous !== url) URL.revokeObjectURL(previous);
    mediaCache.delete(key);
    mediaCache.set(key, url);
    while (mediaCache.size > MAX_MEDIA_CACHE_ENTRIES) {
        const oldestKey = mediaCache.keys().next().value;
        const oldestUrl = mediaCache.get(oldestKey);
        mediaCache.delete(oldestKey);
        if (oldestUrl) URL.revokeObjectURL(oldestUrl);
    }
}

function withVariant(path, variant) {
    const separator = path.includes('?') ? '&' : '?';
    return variant ? `${path}${separator}variant=${encodeURIComponent(variant)}` : path;
}

function legacyImagePath(imageId, enemyId, itemId, variant) {
    if (imageId) return mediaPath(imageId, variant);
    if (enemyId) return withVariant(`/image/enemy/${encodeURIComponent(enemyId)}`, variant);
    if (itemId) return withVariant(`/image/item/${encodeURIComponent(itemId)}`, variant);
    return null;
}

async function fetchMedia(cacheId, variant, path) {
    const key = mediaCacheKey(cacheId, variant);
    if (mediaCache.has(key)) {
        const cached = mediaCache.get(key);
        mediaCache.delete(key);
        mediaCache.set(key, cached);
        return cached;
    }
    if (pendingMedia.has(key)) return pendingMedia.get(key);

    const request = apiGet(path)
        .then(async (response) => {
            if (!response?.ok) return null;
            const objectUrl = URL.createObjectURL(await response.blob());
            cacheMedia(key, objectUrl);
            return objectUrl;
        })
        .finally(() => pendingMedia.delete(key));

    pendingMedia.set(key, request);
    return request;
}

async function fetchMediaStream(mediaId, variant) {
    const path = `/media/${encodeURIComponent(mediaId)}/stream-ticket?variant=${encodeURIComponent(variant)}`;
    const response = await apiPost(path, null, true);
    if (!response?.ok) return null;
    const ticket = await response.json();
    const expiresAt = Date.parse(ticket?.expiresAtUtc);
    if (!ticket?.path || !Number.isFinite(expiresAt) || expiresAt <= Date.now()) return null;
    return { url: buildApiUrl(ticket.path), expiresAt };
}

function usePrefersReducedMotion() {
    const [reducedMotion, setReducedMotion] = useState(() => (
        typeof window !== 'undefined'
        && typeof window.matchMedia === 'function'
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ));

    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
        const query = window.matchMedia('(prefers-reduced-motion: reduce)');
        const update = (event) => setReducedMotion(event.matches);
        query.addEventListener?.('change', update);
        query.addListener?.(update);
        return () => {
            query.removeEventListener?.('change', update);
            query.removeListener?.(update);
        };
    }, []);

    return reducedMotion;
}

export function clearMediaCache() {
    mediaCache.forEach((url) => URL.revokeObjectURL(url));
    mediaCache.clear();
    pendingMedia.clear();
}

export function ImageLoader({
    imageId,
    enemyId,
    itemId,
    animatedMediaId,
    animatedImageId,
    variant,
    posterVariant,
    videoVariant = MEDIA_VARIANTS.VIDEO_PREVIEW,
    size,
    alt = '',
    title,
    placeholder,
    className = '',
    alwaysAnimate = false,
    staticPreview = false,
    videoProps,
    videoControls = false,
    width,
    height,
    aspectRatio,
}) {
    const resolvedVariant = resolveMediaVariant(variant, size);
    const animationId = animatedMediaId || animatedImageId;
    const resolvedPosterVariant = posterVariant
        || (resolvedVariant === MEDIA_VARIANTS.REGION || resolvedVariant === MEDIA_VARIANTS.HERO
            ? MEDIA_VARIANTS.REGION_POSTER
            : MEDIA_VARIANTS.ENTITY_POSTER);
    const posterId = imageId || enemyId || itemId || animationId;
    const posterPath = imageId || enemyId || itemId
        ? legacyImagePath(imageId, enemyId, itemId, resolvedVariant)
        : mediaPath(animationId, resolvedPosterVariant);
    const posterCacheVariant = imageId || enemyId || itemId ? resolvedVariant : resolvedPosterVariant;
    const posterKey = mediaCacheKey(posterId, posterCacheVariant);
    const animationKey = mediaCacheKey(animationId, videoVariant);
    const [src, setSrc] = useState(null);
    const [loadedPosterKey, setLoadedPosterKey] = useState(null);
    const [posterReady, setPosterReady] = useState(false);
    const [animatedStream, setAnimatedStream] = useState(null);
    const [hovered, setHovered] = useState(false);
    const [focused, setFocused] = useState(false);
    const [manualPlaying, setManualPlaying] = useState(false);
    const [userPaused, setUserPaused] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const reducedMotion = usePrefersReducedMotion();
    const videoRef = useRef(null);
    const currentSrc = loadedPosterKey === posterKey ? src : null;
    const currentAnimatedSrc = animatedStream?.key === animationKey
        && animatedStream.expiresAt > Date.now() + 5000
        ? animatedStream.url
        : null;
    const currentPosterReady = loadedPosterKey === posterKey && posterReady;
    const wantsVideo = Boolean(
        !staticPreview
        && animationId
        && (hovered || focused || manualPlaying || alwaysAnimate),
    );
    const shouldPlay = Boolean(
        !staticPreview
        && currentPosterReady
        && currentAnimatedSrc
        && (manualPlaying || (!reducedMotion && !userPaused && (hovered || focused || alwaysAnimate))),
    );
    const [intrinsicWidth, intrinsicHeight] = mediaDimensions(posterCacheVariant);
    const ratio = aspectRatio || `${intrinsicWidth} / ${intrinsicHeight}`;
    const containerStyle = useMemo(() => ({
        '--media-aspect-ratio': ratio,
        '--media-intrinsic-width': `${intrinsicWidth}px`,
        ...(width ? { width } : {}),
        ...(height ? { height } : {}),
    }), [height, intrinsicWidth, ratio, width]);

    useEffect(() => {
        let active = true;
        setSrc(null);
        setLoadedPosterKey(posterKey);
        setPosterReady(false);
        if (posterId && posterPath) {
            fetchMedia(posterId, posterCacheVariant, posterPath)
                .then((url) => active && setSrc(url))
                .catch(() => active && setSrc(null))
                .finally(() => active && setPosterReady(true));
        } else {
            setPosterReady(true);
        }
        return () => { active = false; };
    }, [posterCacheVariant, posterId, posterKey, posterPath]);

    useEffect(() => {
        let active = true;
        if (!wantsVideo || !animationId) return () => { active = false; };
        if (currentAnimatedSrc) return () => { active = false; };
        fetchMediaStream(animationId, videoVariant)
            .then((stream) => {
                if (!active) return;
                setAnimatedStream(stream ? { ...stream, key: animationKey } : null);
            })
            .catch(() => {
                if (!active) return;
                setAnimatedStream(null);
            });
        return () => { active = false; };
    }, [animationId, animationKey, currentAnimatedSrc, videoVariant, wantsVideo]);

    useEffect(() => {
        setManualPlaying(false);
        setUserPaused(false);
        setIsPlaying(false);
    }, [animationKey]);

    useEffect(() => {
        const video = videoRef.current;
        if (!video || !shouldPlay) {
            setIsPlaying(false);
            return;
        }
        video.play()
            .then(() => setIsPlaying(true))
            .catch(() => setIsPlaying(false));
    }, [currentAnimatedSrc, shouldPlay]);

    const stopAutomaticPreview = () => {
        if (!manualPlaying) setUserPaused(false);
    };

    const togglePlayback = (event) => {
        event.stopPropagation();
        if (isPlaying || shouldPlay) {
            setManualPlaying(false);
            setUserPaused(true);
            videoRef.current?.pause();
        } else {
            setManualPlaying(true);
            setUserPaused(false);
        }
    };

    const showingVideo = shouldPlay && currentAnimatedSrc;
    const mediaClassName = [
        'media-loader',
        !className && !width ? 'media-loader--intrinsic' : '',
        className,
    ].filter(Boolean).join(' ');

    return (
        <span
            className={mediaClassName}
            style={containerStyle}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => { setHovered(false); stopAutomaticPreview(); }}
            onFocus={() => setFocused(true)}
            onBlur={(event) => {
                if (event.currentTarget.contains(event.relatedTarget)) return;
                setFocused(false);
                stopAutomaticPreview();
            }}
        >
            {currentSrc ? (
                <img
                    className="media-loader__media"
                    src={currentSrc}
                    alt={alt}
                    title={title}
                    width={width || intrinsicWidth}
                    height={height || intrinsicHeight}
                    loading="lazy"
                />
            ) : placeholder !== 'none' ? (
                <span className="media-loader__media img-placeholder" aria-hidden="true" />
            ) : null}
            {showingVideo && (
                <video
                    {...videoProps}
                    ref={videoRef}
                    className="media-loader__media"
                    src={currentAnimatedSrc}
                    width={width || intrinsicWidth}
                    height={height || intrinsicHeight}
                    muted
                    playsInline
                    referrerPolicy="no-referrer"
                    controls={videoControls}
                    loop={videoProps?.loop ?? true}
                    autoPlay={false}
                    aria-hidden="true"
                    tabIndex={-1}
                    onPlay={() => setIsPlaying(true)}
                    onPause={() => setIsPlaying(false)}
                />
            )}
            {!staticPreview && animationId && (
                <button
                    type="button"
                    className="media-loader__control"
                    onClick={togglePlayback}
                    onPointerDown={(event) => event.stopPropagation()}
                    draggable={false}
                    aria-label={`${isPlaying || shouldPlay ? 'Pause' : 'Play'} animated media`}
                    aria-pressed={isPlaying || shouldPlay}
                    title={`${isPlaying || shouldPlay ? 'Pause' : 'Play'} animated media`}
                >
                    <span aria-hidden="true">{isPlaying || shouldPlay ? '❚❚' : '▶'}</span>
                </button>
            )}
        </span>
    );
}
